import React, { useMemo } from 'react';

import { Card } from '@ergeon/core-components';

import useCallControls from '../../hooks/useCallControls';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';

import { useActiveCalls, useDraggable, useFloatingAnimation, getAnimationClasses } from './hooks';
import DragHandle from './DragHandle';
import FloatingCallItem from './FloatingCallItem';

const FloatingCallPanel: React.FC = () => {
  const { activeCalls, hasActiveCalls } = useActiveCalls();
  const { shouldAnimate } = useFloatingAnimation(hasActiveCalls);
  const { ref, isDragging, handleMouseDown } = useDraggable();

  // Get the first active call for keyboard shortcuts
  const firstCall = activeCalls[0];
  const callControls = useCallControls(firstCall);

  // Register keyboard shortcuts for call controls
  const shortcuts = useMemo(() => {
    if (!firstCall || !callControls) return [];

    const { status, toggleMute, holdCall, accept, decline, hangUp } = callControls;
    const isActive = ['connecting', 'open', 'reconnecting', 'ringing'].includes(status || '');
    const isPending = status === 'pending';

    const allShortcuts = [];

    // Space: Accept or Decline incoming calls
    if (isPending) {
      allShortcuts.push({
        key: ' ',
        description: 'Accept incoming call',
        category: 'calls' as const,
        action: accept,
      });
      allShortcuts.push({
        key: 'd',
        description: 'Decline incoming call',
        category: 'calls' as const,
        action: decline,
      });
    }

    // M: Toggle mute (only for active calls)
    if (isActive) {
      allShortcuts.push({
        key: 'm',
        description: 'Toggle mute',
        category: 'calls' as const,
        action: toggleMute,
      });
    }

    // H: Toggle hold (only for active calls)
    if (isActive) {
      allShortcuts.push({
        key: 'h',
        description: 'Toggle hold',
        category: 'calls' as const,
        action: holdCall,
      });
    }

    // Cmd/Ctrl + H: Hangup (for active or pending calls)
    if (isActive || isPending) {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      allShortcuts.push({
        key: 'h',
        [isMac ? 'metaKey' : 'ctrlKey']: true,
        description: 'Hang up call',
        category: 'calls' as const,
        action: () => {
          if (window.confirm('Are you sure you want to hang up this call?')) {
            hangUp();
          }
        },
      });
    }

    return allShortcuts;
  }, [firstCall, callControls]);

  useKeyboardShortcuts({
    id: 'call-controls',
    shortcuts,
    priority: 10, // Higher priority than navigation
    enabled: hasActiveCalls,
  });

  if (!hasActiveCalls) {
    return null;
  }

  return (
    <Card
      className={`fixed bottom-6 left-6 z-50 flex flex-col max-w-md w-full ${
        isDragging ? '' : 'transition-transform duration-500 ease-out'
      } ${getAnimationClasses(shouldAnimate)}`}
      flavor="transparent"
      onMouseDown={handleMouseDown}
      ref={ref}
      style={{
        userSelect: isDragging ? 'none' : 'auto',
      }}>
      <DragHandle />
      <div className="flex flex-col gap-1">
        {activeCalls.map((call) => (
          <FloatingCallItem call={call} key={call.parameters.CallSid} />
        ))}
      </div>
    </Card>
  );
};

export default FloatingCallPanel;
